import { Fragment } from "react";

// Explanation markup is the format of word explanations, defined in
// api/src/explanationmarkup/explanationmarkup.go: text with <r>...</r> pieces
// in the learned language, <l>...</l> pieces in the spoken language, and XML
// escapes. This module parses it the same way and renders it.

export type ExplanationSegmentKind = "" | "r" | "l";

export interface ExplanationSegment {
  // The tag of a marked piece, or "" for unmarked text.
  kind: ExplanationSegmentKind;
  text: string;
}

const TAGS = ["<r>", "</r>", "<l>", "</l>"];

const ESCAPES: [string, string][] = [
  ["&lt;", "<"],
  ["&gt;", ">"],
  ["&amp;", "&"],
  ["&quot;", '"'],
  ["&apos;", "'"],
];

function tagAt(markup: string, i: number): string | undefined {
  return TAGS.find((tag) => markup.startsWith(tag, i));
}

// unescapeAt decodes the escape starting at markup[i]: its text and length.
// An "&" that starts no escape is itself.
function unescapeAt(markup: string, i: number): [string, number] {
  const escape = ESCAPES.find(([escaped]) => markup.startsWith(escaped, i));
  return escape ? [escape[1], escape[0].length] : ["&", 1];
}

// parseExplanationMarkup decodes markup into segments, or returns null when
// it isn't valid markup. Empty pieces are dropped.
export function parseExplanationMarkup(
  markup: string,
): ExplanationSegment[] | null {
  const segments: ExplanationSegment[] = [];
  let kind: ExplanationSegmentKind = "";
  let text = "";
  const endSegment = () => {
    if (text) {
      segments.push({ kind, text });
      text = "";
    }
  };
  for (let i = 0; i < markup.length; ) {
    const char = markup[i];
    if (char === "<") {
      const tag = tagAt(markup, i);
      if (!tag) return null;
      const tagKind = tag.replace(/[</>]/g, "") as ExplanationSegmentKind;
      if (tag.startsWith("</")) {
        if (kind !== tagKind) return null;
        endSegment();
        kind = "";
      } else {
        if (kind !== "") return null;
        endSegment();
        kind = tagKind;
      }
      i += tag.length;
    } else if (char === ">") {
      return null;
    } else if (char === "&") {
      const [decoded, length] = unescapeAt(markup, i);
      text += decoded;
      i += length;
    } else {
      text += char;
      i++;
    }
  }
  if (kind !== "") return null;
  endSegment();
  return segments;
}

// explanationPlainText is the text of markup without its marks. It accepts
// invalid markup too: tags are dropped wherever they are and stray "<" and
// ">" are kept as text.
export function explanationPlainText(markup: string): string {
  let text = "";
  for (let i = 0; i < markup.length; ) {
    const tag = markup[i] === "<" ? tagAt(markup, i) : undefined;
    if (tag) {
      i += tag.length;
    } else if (markup[i] === "&") {
      const [decoded, length] = unescapeAt(markup, i);
      text += decoded;
      i += length;
    } else {
      text += markup[i];
      i++;
    }
  }
  return text;
}

// escapeExplanationText turns plain text into markup with no marked pieces.
export function escapeExplanationText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// ExplanationText renders explanation markup: pieces in the learned language
// in bold italic, translations in italic. Both stay in the surrounding UI
// font, whose bold italic is reliable everywhere. Invalid markup, which the
// API never sends, is shown as plain text.
export function ExplanationText({ markup, r }: { markup: string; r: string }) {
  const segments = parseExplanationMarkup(markup) ?? [
    { kind: "", text: explanationPlainText(markup) },
  ];
  return (
    <>
      {segments.map((segment, idx) =>
        segment.kind === "r" ? (
          <i key={idx} lang={r} className="font-semibold">
            {segment.text}
          </i>
        ) : segment.kind === "l" ? (
          <i key={idx}>{segment.text}</i>
        ) : (
          <Fragment key={idx}>{segment.text}</Fragment>
        ),
      )}
    </>
  );
}
